import React, { memo, useCallback } from 'react';

import { useRecoilValue } from 'recoil';
import { isEmpty } from 'lodash';

import {
  blockInfoGraphicType,
  blockSelectedPeerGroupId,
  customizedBlock,
  isReportState,
  recoilBlockContentSize,
  type StudioAnalysis,
} from 'venn-state';

import { useFilteredRequests } from '../../utils/hooks/use-filtered-requests';

import { useAppPrintMode } from '../../../print';
import {
  BOLD_CLASS,
  DARK_COLUMN_CLASS,
  DATA_TYPE_EXCEL_STYLES,
  useSetBlockSize,
  WATERMARK_POSITION_TOP,
} from '../../../studio-blocks';

import { useBlockContext } from '../../contexts/block-context';
import { usePeerGroupChartHeight } from '../../../studio-blocks/components/charts/peer-groups/usePeerGroupChartHeight';
import { useIsBlockGridCompact } from '../../../studio-blocks/logic/useIsBlockGridCompact';
import { useCommonGridProps } from '../../../studio-blocks/logic/columnUtils';
import { usePeerGroupsGrid } from '../../../studio-blocks/components/charts/peer-groups/usePeerGroupsGrid';
import StyledEmptyState from '../../../studio-blocks/components/StyledEmptyState';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import { GridWrapper } from '../../../studio-blocks/common';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import type { ColDef } from 'ag-grid-community';
import type { Analysis } from 'venn-api';
import { PeerGroupBoxChart } from '../../../studio-blocks/components/charts/peer-groups/PeerGroupBoxChart';
import { PeerGroupPercentileRankChart } from '../../../studio-blocks/components/charts/peer-groups/PeerGroupPercentileRankChart';
import { PGA_RANK_DISCLAIMER } from '../../../studio-blocks/components/charts/peer-groups';
import { DownloadableContentBlock } from '../../../content-block';
import { EmptyHeaderSpace } from '../../../content-block/DownloadableContentBlock';

interface PeerGroupAnalysisContentProps {
  id: string;
}

const FOOTER_HEIGHT_PX = 28;
const DISCLAIMER_HEIGHT_PX = 16;

const gridOverrides: ColDef = {
  cellClassRules: {
    [DARK_COLUMN_CLASS]: ({ data }) => {
      return data?.isMetadata;
    },
    [BOLD_CLASS]: ({ data }) => {
      return data?.isMetadata;
    },
  },
};

export const PeerGroupAnalysisContent = ({ id }: PeerGroupAnalysisContentProps) => {
  const { inPrintModeOrReportLab: inPrintMode } = useAppPrintMode();

  const { analyses } = useFilteredRequests(id);
  const data = analyses?.[0]?.[0]?.peerGroupAnalysis;
  const errorMessage = (analyses?.[0]?.[0] as unknown as StudioAnalysis | undefined)?.globalError?.text;
  const isReport = useRecoilValue(isReportState);

  const { relativeToBenchmark } = useRecoilValue(customizedBlock(id));
  const selectedPeerGroup = useRecoilValue(blockSelectedPeerGroupId(id));
  const isCompact = useIsBlockGridCompact();
  const metrics = useSelectedMetrics();
  const { blockRef: downloadableContentRef } = useBlockContext();

  if (!selectedPeerGroup) {
    return (
      <StyledEmptyState
        selectedRefId={id}
        header="No peer group selected"
        message="Please select a peer group in the settings panel"
      />
    );
  }
  if (isEmpty(metrics)) {
    return (
      <StyledEmptyState
        selectedRefId={id}
        header="No metrics selected"
        message="Please select metrics in the settings panel"
      />
    );
  }

  if (!data) {
    return (
      <StyledEmptyState selectedRefId={id} header="Unable to run analysis" message={errorMessage ?? 'Chart is empty'} />
    );
  }

  const peerGroupsBlockComponent = (
    <div className="[&_.ag-header-group-cell]:p-[2px_6px_2px_2px]">
      <Chart id={id} data={data} />
      <div className="border border-solid border-venn-grey" />
      <Grid
        id={id}
        data={data}
        inPrintMode={inPrintMode}
        isCompact={isCompact}
        exportable={!!analyses?.[0]?.[0]?.exportable?.[0]}
        isReport={isReport}
      />
      <div className={`h-[${FOOTER_HEIGHT_PX}px] items-center flex p-2 text-[12px] font-bold`}>
        Peer Group: &nbsp;
        <div className="font-bold">{selectedPeerGroup.categoryName}</div>
      </div>
      <div
        className={`flex h-[${DISCLAIMER_HEIGHT_PX}px] py-[12px] px-[8px] border-t border-venn-grey text-[10px] flex-col justify-center items-start self-stretch`}
      >
        {PGA_RANK_DISCLAIMER}
      </div>
    </div>
  );

  if (inPrintMode) {
    return peerGroupsBlockComponent;
  }

  return (
    <DownloadableContentBlock
      header={<EmptyHeaderSpace />}
      noBorder
      downloadable={{
        png: true,
        options: {
          fileName: 'Peer Group',
          watermark: {
            top: WATERMARK_POSITION_TOP,
            right: 20,
          },
        },
        tracking: {
          description: 'BUILDER',
          userUploaded: false,
          relativeToBenchmark,
          subjectType: undefined,
          subjectId: undefined,
        },
        disabled: false,
        target: downloadableContentRef,
      }}
    >
      {peerGroupsBlockComponent}
    </DownloadableContentBlock>
  );
};

const Grid = memo(function InternalGrid({
  id,
  data,
  inPrintMode,
  isCompact = false,
  exportable,
  isReport,
}: {
  id: string;
  data: Analysis['peerGroupAnalysis'];
  inPrintMode: boolean;
  isCompact?: boolean;
  exportable: boolean;
  isReport: boolean;
}) {
  const { rowData, columnDefs } = usePeerGroupsGrid(data!);

  const commonGridProps = useCommonGridProps(gridOverrides);
  const chartComponentHeight = usePeerGroupChartHeight();

  /** One of the few rare times we use raw state, because it is derived from an ag-grid event.
   *  Passing this callback to ag-grid is necessary, as we need to factor the chart into account, too */
  const setRawBlockContentSize = useSetBlockSize(recoilBlockContentSize.rawState(id));
  const onGridSizeChange = useCallback(
    (height: number, width: number) => {
      setRawBlockContentSize({
        bounds: {
          height: height + chartComponentHeight + FOOTER_HEIGHT_PX + DISCLAIMER_HEIGHT_PX,
          width,
        },
      });
    },
    [setRawBlockContentSize, chartComponentHeight],
  );

  return (
    <GridWrapper isReport={isReport}>
      <ExportableGrid
        {...commonGridProps}
        exportable={exportable}
        selectedRefId={id}
        inPrintMode={inPrintMode}
        isCompact={isCompact}
        rowData={rowData}
        columnDefs={columnDefs}
        excelStyles={DATA_TYPE_EXCEL_STYLES}
        tooltipShowDelay={1200}
        suppressRowVirtualisation
        suppressRowTransform
        scrollbarWidth={10}
        domLayout={'autoHeight' as const}
        onGridSizeChange={onGridSizeChange}
      />
    </GridWrapper>
  );
});

const Chart = memo(function InternalChart({ id, data }: { id: string; data: Analysis['peerGroupAnalysis'] }) {
  const infoGraphicType = useRecoilValue(blockInfoGraphicType(id));

  return infoGraphicType === 'BOX_CHART' ? (
    <PeerGroupBoxChart data={data!} />
  ) : (
    <PeerGroupPercentileRankChart data={data!} />
  );
});
